import { styled } from "@linaria/react"
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading5 } from "@/components/Heading/Heading.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Toggle } from "@/components/Toggle/Toggle.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { DevServer } from "@/iframe/lib/proto.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { TaskPreviewTabModel } from "@/routes/tasks/TaskViewScene/TaskPreviewTabModel.tsx"


interface DevServerStatusPopupProps {
  devServer: DevServer | undefined
  previewTabModel: TaskPreviewTabModel
}

export const DevServerStatusPopup = observer(({ devServer, previewTabModel }: DevServerStatusPopupProps) => {
  const [isOpen, setIsOpen] = useState(false)

  if (devServer === undefined) return null

  const isKnownDevServer = devServer === DevServer.Vite || devServer === DevServer.Webpack
  const devServerName = devServer === DevServer.Vite ? "Vite" : devServer === DevServer.Webpack ? "Webpack" : "unknown"

  const description = isKnownDevServer
    ? `This page is served by ${devServerName} development server`
    : "This page is served by an unknown server"

  const hotReload = previewTabModel.hotReload.get()

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <StyledPopoverTrigger>
        <Tooltip content={isOpen ? undefined : `${description}. Hot reload: ${devServerName === "unknown" ? "n/a" : (hotReload ? "on" : "off")}`}>
          {devServer === DevServer.Unknown ? <GreyDot /> : <GreenDot />}
        </Tooltip>
      </StyledPopoverTrigger>
      <PopoverPortal>
        <StyledPopoverContent
          className={DropdownMenuStyles.Content}
          align="start"
          side="bottom"
          alignOffset={-16}
          sideOffset={16}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Vertical gap="16px">
            <Vertical gap="8px">
              <Heading5>Dev Server</Heading5>
              <Description>
                {description}
              </Description>
            </Vertical>

            <Horizontal alignItems="center" gap="8px">
              <Toggle
                active={devServer !== DevServer.Unknown && hotReload}
                onClick={() => previewTabModel.toggleHotReload()}
                disabled={!isKnownDevServer}
              />
              <ToggleLabel disabled={!isKnownDevServer}>Use hot reload</ToggleLabel>
            </Horizontal>
          </Vertical>
        </StyledPopoverContent>
      </PopoverPortal>
    </Popover>
  )
})

const Description = styled(SecondaryTextSpan)`
  ${textStyles.smallFontSize};
`

const ToggleLabel = styled.label<{ disabled: boolean }>`
  ${textStyles.smallFontSize};

  opacity: ${props => props.disabled ? 0.5 : 1};
`

const StyledPopoverTrigger = styled(PopoverTrigger)`
  margin: 0 -8px 0 0;
  padding: 0;
`

const StyledPopoverContent = styled(PopoverContent)`
  padding: 16px;

  border-radius: var(--border-radius-20);
  background-color: var(--text-color-2-opaque);

  z-index: var(--z-index-dropdown);
`

const DevServerStatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: var(--border-radius-round);
  flex-shrink: 0;
  cursor: pointer;
`

const GreyDot = styled(DevServerStatusDot)`
  background-color: var(--secondary-text-color);
`

const GreenDot = styled(DevServerStatusDot)`
  background-color: var(--success-color);
`